import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createGrid,
  type ColDef,
  type GridApi,
  type GridOptions,
  type ICellRendererParams,
  type IDatasource,
  type IGetRowsParams,
} from 'ag-grid-community';
import { renderKfPage, kfJsonPayload, KF_ICON, type KfPage } from '@vario-connect/frontend-design';

interface LogCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface LogSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Embedded-Slot-Renderer fuer den Vario-App-Tab `system.apps.{appId}.tabs.log`.
 *  Rendert das Log-Fenster (kf-page) direkt in den Slot-Container (volle iframe-Hoehe). */
export function renderLog(ctx: LogSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  renderLogTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl });
}

interface LogRow {
  id: number;
  event: string;
  status: string; // 'success' | 'error'
  storefront: string | null;
  ref_id: string | null;
  script_name: string | null;
  summary: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

const GRID_BLOCK_SIZE = 50;

/** Status-Segmente des Toolbar-Filters (Design: Alle / Erfolg / Warnung / Fehler).
 *  `id` ist zugleich der DB-Status-Wert (ausser 'all'). 'warning' existiert in den
 *  Daten aktuell nicht — das Segment bleibt (Design-treu) und zeigt dann 0. */
const SEGMENTS = [
  { id: 'all', label: 'Alle' },
  { id: 'success', label: 'Erfolg', color: 'var(--vario-green)' },
  { id: 'warning', label: 'Warnung', color: 'var(--warn)' },
  { id: 'error', label: 'Fehler', color: 'var(--danger)' },
];

/** Aktivitaets-Log der Kaufland-App im neuen go-eCommerce-Fenster-Design (kf-page).
 *  Tabelle "was ist passiert" — pro Vorgang eine Zeile; Detail-Drawer zeigt das
 *  eingereichte JSON + Ergebnis/Fehler, Fehlversuche bieten "Erneut verarbeiten". */
export function renderLogTab(host: HTMLElement, ctx: LogCtx, opts: { embedded?: boolean } = {}): void {
  const page = renderKfPage(host, {
    eyebrow: 'Kaufland · Protokoll',
    title: 'Log-Übersicht',
    status: 'Kaufland verbunden',
    actions: [
      { id: 'export', label: 'Exportieren', icon: KF_ICON.export, variant: 'ghost' },
      { id: 'refresh', label: 'Aktualisieren', icon: KF_ICON.refresh, variant: 'primary' },
    ],
    search: { placeholder: 'Referenz, Beleg oder Meldung suchen…' },
    segments: SEGMENTS,
    showColConfig: true,
    fitViewport: !opts.embedded,
  });

  let statusFilter = 'all';
  let searchText = '';

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const { rows, total } = await loadLogPage(ctx, params.startRow, params.endRow, statusFilter, searchText);
        params.successCallback(rows, total);
      } catch (e) {
        console.error('[log] datasource.getRows failed', e);
        params.failCallback();
      }
    },
  };

  const cols: ColDef<LogRow>[] = [
    {
      headerName: 'Zeitpunkt', field: 'created_at', width: 160,
      cellRenderer: (p: ICellRendererParams<LogRow>) => {
        const { date, time } = splitDateTime(p.data?.created_at ?? null);
        return htmlEl(`<span class="c-time">${escapeHtml(date)}, <span class="t2">${escapeHtml(time)}</span></span>`);
      },
    },
    { headerName: 'Vorgang', field: 'event', width: 140, valueFormatter: (p) => eventLabel(p.value) },
    {
      headerName: 'SF', field: 'storefront', width: 70,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(`<span class="c-sf">${escapeHtml(p.data?.storefront ?? '')}</span>`),
    },
    {
      headerName: 'Referenz', field: 'ref_id', width: 130,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(p.data?.ref_id ? `<span class="c-ref">${escapeHtml(p.data.ref_id)}</span>` : ''),
    },
    {
      headerName: 'Skript', field: 'script_name', width: 196,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(`<span class="c-script">${escapeHtml(p.data?.script_name ?? '')}</span>`),
    },
    {
      headerName: 'Status', field: 'status', width: 130,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(statusPill(p.data?.status ?? '')),
    },
    {
      headerName: 'Meldung', field: 'summary', flex: 1, minWidth: 220,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(messageHtml(p.data)),
    },
    {
      headerName: 'Aktionen', colId: 'actions', width: 160, sortable: false, resizable: false, pinned: 'right',
      cellRenderer: () => actionsCell(),
    },
  ];

  const options: GridOptions<LogRow> = {
    columnDefs: cols,
    defaultColDef: { sortable: false, filter: false, resizable: true, suppressHeaderMenuButton: true },
    rowModelType: 'infinite',
    cacheBlockSize: GRID_BLOCK_SIZE,
    maxBlocksInCache: 10,
    infiniteInitialRowCount: 50,
    blockLoadDebounceMillis: 100,
    datasource,
    rowHeight: 46,
    headerHeight: 48,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100],
    animateRows: true,
    suppressCellFocus: true,
    getRowId: (p) => (p.data ? String(p.data.id) : ''),
    onCellClicked: (e) => {
      if (e.colDef.headerName !== 'Aktionen' || !e.data) return;
      const target = e.event?.target as HTMLElement | null;
      const act = target?.closest<HTMLElement>('[data-act]')?.dataset.act;
      if (act === 'reprocess') void retryImport(e.data, ctx, page);
      else openDrawer(e.data, page);
    },
    onModelUpdated: () => updateMeta(),
  };

  // Reihenfolge bewusst: totalCount + gridApi VOR createGrid deklarieren.
  // createGrid kann onModelUpdated synchron feuern (→ updateMeta), bevor die
  // Zuweisung zurueck ist — daher Guard auf gridApi (sonst Laufzeit-TDZ trotz
  // gruenem tsc/vite, siehe CLAUDE.md).
  let totalCount: number | undefined;
  let gridApi: GridApi<LogRow> | undefined;

  function updateMeta(): void {
    if (!gridApi) return;
    const shown = gridApi.getDisplayedRowCount();
    const total = totalCount ?? shown;
    page.setMeta(`<b>${shown}</b> von ${total} Einträgen`);
  }

  const reload = () => gridApi?.setGridOption('datasource', datasource);

  gridApi = createGrid(page.gridEl, options);

  // Status-Zaehler pro Segment laden (Head-Counts) + Gesamtzahl.
  void refreshCounts(ctx, page).then((counts) => {
    totalCount = counts.all;
    updateMeta();
  });

  // ---- toolbar wiring ----
  page.onSegment((id) => {
    statusFilter = id;
    reload();
  });
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  page.onSearch((text) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchText = text.trim();
      reload();
    }, 250);
  });
  page.onAction('refresh', () => {
    // KEIN manuelles showLoadingOverlay: beim Infinite-Row-Model wird ein manuell
    // gezeigtes Overlay nicht automatisch wieder versteckt (kein hideOverlay) und
    // bliebe dauerhaft hängen. reload() (setGridOption datasource) holt frisch und
    // zeigt die ag-Grid-eigenen Lade-Zeilen.
    reload();
    void refreshCounts(ctx, page).then((counts) => {
      totalCount = counts.all;
      updateMeta();
    });
    page.toast('Protokoll aktualisiert');
  });
  page.onAction('export', () => {
    gridApi?.exportDataAsCsv({
      fileName: 'kaufland-log.csv',
      columnKeys: ['created_at', 'event', 'storefront', 'ref_id', 'script_name', 'status', 'summary'],
    });
    page.toast('CSV exportiert');
  });
  page.onAction('cols', () => page.toast('Spalten-Konfiguration folgt'));
}

async function loadLogPage(
  ctx: LogCtx,
  startRow: number,
  endRow: number,
  status: string,
  search: string,
): Promise<{ rows: LogRow[]; total: number | undefined }> {
  let query = ctx.supabase
    .schema('kaufland')
    .from('activity_log')
    .select('id,event,status,storefront,ref_id,script_name,summary,detail,created_at', { count: 'exact' })
    .order('created_at', { ascending: false });
  if (status !== 'all') query = query.eq('status', status);
  if (search) {
    const like = `%${search.replace(/[%,]/g, '')}%`;
    query = query.or(`ref_id.ilike.${like},summary.ilike.${like},script_name.ilike.${like}`);
  }
  const { data, count, error } = await query.range(startRow, endRow - 1);
  if (error) throw error;
  return { rows: (data ?? []) as LogRow[], total: count ?? undefined };
}

async function refreshCounts(ctx: LogCtx, page: KfPage): Promise<{ all: number }> {
  const head = async (status?: string): Promise<number> => {
    let q = ctx.supabase.schema('kaufland').from('activity_log').select('id', { count: 'exact', head: true });
    if (status) q = q.eq('status', status);
    const { count } = await q;
    return count ?? 0;
  };
  const [all, success, warning, error] = await Promise.all([head(), head('success'), head('warning'), head('error')]);
  page.setSegmentCount('all', all);
  page.setSegmentCount('success', success);
  page.setSegmentCount('warning', warning);
  page.setSegmentCount('error', error);
  return { all };
}

/* ---------- cell renderers ---------- */

function htmlEl(html: string): HTMLElement {
  const e = document.createElement('span');
  e.style.display = 'inline-flex';
  e.style.alignItems = 'center';
  e.style.maxWidth = '100%';
  e.innerHTML = html;
  return e;
}

function statusPill(status: string): string {
  if (status === 'success') return `<span class="st st-ok"><span class="pd"></span>Erfolg</span>`;
  if (status === 'error') return `<span class="st st-err"><span class="pd"></span>Fehler</span>`;
  if (status === 'warning') return `<span class="st st-warn"><span class="pd"></span>Warnung</span>`;
  return `<span class="st">${escapeHtml(status)}</span>`;
}

function messageHtml(d: LogRow | undefined): string {
  if (!d?.summary) return '';
  const beleg = belegNumber(d);
  if (d.status === 'success' && beleg) return `<span class="c-msg">Beleg <span class="belg">${escapeHtml(beleg)}</span></span>`;
  return `<span class="c-msg">${escapeHtml(d.summary)}</span>`;
}

function actionsCell(): HTMLElement {
  const e = document.createElement('span');
  e.className = 'act-cell';
  e.innerHTML =
    `<button type="button" class="detail-btn" data-act="detail">${KF_ICON.detail}Detail</button>` +
    `<button type="button" class="act-icon" data-act="reprocess" title="Erneut verarbeiten">${KF_ICON.reprocess}</button>`;
  return e;
}

/* ---------- detail drawer ---------- */

function openDrawer(row: LogRow, page: KfPage): void {
  const { date, time } = splitDateTime(row.created_at);
  const beleg = belegNumber(row);
  page.drawer.open({
    eyebrow: 'Log-Eintrag',
    ref: row.ref_id ?? '—',
    fields: [
      { label: 'Zeitpunkt', html: `${escapeHtml(date)}, ${escapeHtml(time)} Uhr` },
      { label: 'Status', html: statusPill(row.status) },
      { label: 'Vorgang', html: escapeHtml(eventLabel(row.event)) },
      { label: 'Sprache', html: escapeHtml((row.storefront ?? '').toUpperCase()) },
      { label: 'Skript', html: `<span class="c-script" style="font-size:13.5px;color:var(--ink);">${escapeHtml(row.script_name ?? '—')}</span>`, full: true },
      { label: 'Meldung', html: escapeHtml(row.summary ?? '—'), full: true },
    ],
    payloadHtml: kfJsonPayload(row.detail ?? {}),
    footerHtml:
      `<button type="button" class="btn btn-ghost" data-kf-act="copy">${KF_ICON.copy}Payload kopieren</button>` +
      (beleg ? `<button type="button" class="btn btn-primary" data-kf-act="beleg">${KF_ICON.external}Beleg öffnen</button>` : ''),
    onFooter: (id) => {
      if (id === 'copy') {
        void navigator.clipboard?.writeText(JSON.stringify(row.detail ?? {}, null, 2));
        page.toast('Payload kopiert');
      } else if (id === 'beleg') {
        page.toast('Beleg wird geöffnet…');
      }
    },
  });
}

/* ---------- reprocess (Funktion erhalten: vario_enqueue_order_create) ---------- */

async function retryImport(row: LogRow, ctx: LogCtx, page: KfPage): Promise<void> {
  // "Erneut verarbeiten" ist (vorerst) nur für Auftragsimporte verdrahtet (vario_enqueue_order_create).
  // Artikelimport-Zeilen dürfen NICHT denselben RPC triggern.
  if (row.event !== 'order_import') {
    page.toast('Erneut verarbeiten ist aktuell nur für Aufträge verfügbar.');
    return;
  }
  if (!row.storefront || !row.ref_id) {
    page.toast('Erneut verarbeiten nicht möglich — keine Referenz.');
    return;
  }
  try {
    const { data, error } = await ctx.supabase.rpc('vario_enqueue_order_create', {
      _storefront: row.storefront,
      _id_order: row.ref_id,
    });
    if (error) throw error;
    const reused = Array.isArray(data) ? data[0]?.reused : (data as { reused?: boolean } | null)?.reused;
    page.toast(reused ? `${row.ref_id} läuft bereits.` : `${row.ref_id} wird erneut verarbeitet…`);
  } catch (e) {
    page.toast(`Erneuter Start fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
  }
}

/* ---------- helpers ---------- */

function belegNumber(row: LogRow): string | null {
  const d = row.detail as Record<string, unknown> | null;
  const num = d?.['documentNumber'] ?? d?.['document_number'];
  if (typeof num === 'string' && num) return num;
  if (typeof num === 'number') return String(num);
  const m = row.summary?.match(/Beleg\s+(\S+)/i);
  return m ? m[1]! : null;
}

function eventLabel(event: string): string {
  if (event === 'order_import') return 'Auftragsimport';
  if (event === 'article_import') return 'Artikelimport';
  if (event === 'listing_publish') return 'Aufschalten';
  return event;
}

function splitDateTime(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' };
  return {
    date: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    time: d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
